import React, { ChangeEvent } from 'react';
import { Autocomplete, Grid, TextField, Typography } from '@mui/material';
import { StripeTaxCountryOption } from '../../../reference-data/stripeSupportedTaxIds';
import { FormControl } from '../../../utils/form.utils';

interface StripeTaxIdProps {
    taxIdFormControl: FormControl<string>;
    businessNameFormControl: FormControl<string>;
    businessCountryFormControl: FormControl<StripeTaxCountryOption | undefined>;
    countryOptions: StripeTaxCountryOption[];
    removeTaxIdErrorMessage: () => void;
}

export const StripeTaxId = ({
    taxIdFormControl,
    businessNameFormControl,
    businessCountryFormControl,
    countryOptions,
    removeTaxIdErrorMessage,
}: StripeTaxIdProps) => {
    return (
        <Grid container spacing={2}>
            <Grid size={12}>
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                    }}
                >
                    We need your business details for invoicing purposes.
                </Typography>
            </Grid>
            <Grid size={12}>
                <TextField
                    id="business-name-input"
                    fullWidth
                    error={businessNameFormControl?.showError}
                    helperText={
                        businessNameFormControl?.showError
                            ? businessNameFormControl?.validationMessage
                            : undefined
                    }
                    type="text"
                    label="Business name"
                    autoComplete="off"
                    value={businessNameFormControl?.value}
                    onBlur={businessNameFormControl?.handleBlur}
                    onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                        const val: string = e?.target?.value;
                        businessNameFormControl?.setValue(val);
                        removeTaxIdErrorMessage();
                    }}
                />
            </Grid>
            <Grid size={12}>
                <Autocomplete<StripeTaxCountryOption>
                    id="business-country-select"
                    options={countryOptions}
                    value={businessCountryFormControl?.value ?? null}
                    isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                    }
                    getOptionLabel={(option) => option.label}
                    onBlur={businessCountryFormControl?.handleBlur}
                    onChange={(_, option): void => {
                        businessCountryFormControl?.setValue(
                            option ?? undefined
                        );
                        removeTaxIdErrorMessage();
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Where is the business registered?"
                            placeholder="Select country"
                            error={businessCountryFormControl?.showError}
                            helperText={
                                businessCountryFormControl?.showError
                                    ? businessCountryFormControl?.validationMessage
                                    : undefined
                            }
                        />
                    )}
                />
            </Grid>
            <Grid size={12}>
                <TextField
                    id="tax-id-input"
                    fullWidth
                    error={taxIdFormControl?.showError}
                    helperText={
                        taxIdFormControl?.showError
                            ? taxIdFormControl?.validationMessage
                            : 'If using a VAT (value-added tax) number, include the 2-letter country code.'
                    }
                    type="text"
                    label="Tax identification number or VAT number"
                    autoComplete="off"
                    value={taxIdFormControl?.value}
                    onBlur={taxIdFormControl?.handleBlur}
                    onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                        const val: string = e?.target?.value;

                        if (taxIdFormControl?.showError) {
                            removeTaxIdErrorMessage();
                        }

                        taxIdFormControl?.setValue(val);
                    }}
                />
            </Grid>
        </Grid>
    );
};
